import {formatUserDefinedObjectAdditionalColumnValue} from "~/domainModels/userDefinedObjectAdditionalColumnValues";
import {UserDefinedObjectTemplateAdditionalColumnType as UdoColumnType} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplateAdditionalColumn";
import type {CapabilityUdoColumnResolver} from "~/utilities/capabilityUdoTemplateColumns";
import {useCapabilityUdoColumns} from "~/contexts/workspaceContext";
import CalendarIcon from "@atlaskit/icon/core/calendar";
import CopyIcon from "@atlaskit/icon/core/copy";
import EditIcon from "@atlaskit/icon/core/edit";
import InformationCircleIcon from "@atlaskit/icon/core/information-circle";
import LinkExternalIcon from "@atlaskit/icon/core/link-external";
import NoteIcon from "@atlaskit/icon/core/note";
import PersonAvatarIcon from "@atlaskit/icon/core/person-avatar";
import SubtasksIcon from "@atlaskit/icon/core/subtasks";
import TagIcon from "@atlaskit/icon/core/tag";
import type {NewCoreIconProps, NewIconProps} from "@atlaskit/icon/dist/types/types";
import {token} from "@atlaskit/tokens";
import {AnankeTempCollapsibleSectionWithIcon} from "~/components/anankeTemp/anankeTempCollapsibleSectionWithIcon";
import {AnankeTempFormCollapsibleSection} from "~/components/anankeTemp/anankeTempFormCollapsibleSection";
import {WhatsappIcon} from "~/components/svgs";
import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";

import {eventContainerEditPanelIconsMap} from "~/domainModels/eventContainerPanelIcons";

import {OttopilotEventContainerViewPanelSectionKind} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainerTemplateConfiguration";
import type {EventContainerTemplate as EventContainerTemplateConfiguration} from "~/domainModels/workspaceModules/eventContainersModule";
import {AnankeHeading} from "@vesta/ananke/components/anankeHeading";
import {AnankeSkeleton} from "@vesta/ananke/components/anankeSkeleton";
import {AnankeAvatar} from "@vesta/ananke/components/anankeAvatar";
import {AnankeIconButton} from "@vesta/ananke/components/anankeIconButton";
import {AnankeLabel} from "@vesta/ananke/components/anankeLabel";
import {AnankeLinkIconButton} from "@vesta/ananke/components/anankeLinkIconButton";
import {AnankeSimpleTag} from "@vesta/ananke/components/anankeSimpleTag";
import {AnankeTab} from "@vesta/ananke/components/anankeTab";
import {AnankeTabList} from "@vesta/ananke/components/anankeTabList";
import {AnankeTabPanel} from "@vesta/ananke/components/anankeTabPanel";
import {AnankeTabs} from "@vesta/ananke/components/anankeTabs";
import {AnankeText} from "@vesta/ananke/components/anankeText";
import {AnankeTempVerticalSpacer} from "~/components/anankeTemp/anankeTempVerticalSpacer";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {ItemBuilder} from "@vesta/global-common-typescript/components/itemBuilder";
import type {EventContainerWithEvents} from "~/utilities/apis/api";
import {formatDateTime, getUserDisplayName, getUserRole} from "~/utilities/utilities";
import {processOttopilotStringTemplate} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotStringTemplate";
import {eventContainerStatusToDisplayName} from "~/utilities/domainStatusDisplayNames";
import {buildJourneyProfilePath} from "~/utilities/entityProfileNavigation";
import {useNavigate} from "@tanstack/react-router";
import {useOrganizationalUnitTemplates, useProductCatalogueTemplates} from "~/contexts/workspaceContext";
import {useOrganizationalUnits} from "~/contexts/organizationalUnitsContext";
import {useWorkspaceUserTemplates as useUserTemplates} from "~/contexts/workspaceContext";
import {useUsers} from "~/contexts/workspaceUsersContext";
import {useWorkspace} from "~/contexts/workspaceContext";
import {useProductCatalogue} from "~/contexts/productCatalogueContext";
import {useEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode} from "react";
import type {JSX} from "react";
import ArrowUpRightIcon from "@atlaskit/icon/core/arrow-up-right";
import {
    AMARON__EVENT__ENQUIRY__ENQUIRY_STATUS__COLUMN_ID,
    AMARON__EVENT_CONTAINER__LAST_ACTIVITY__COLUMN_ID,
    AMARON__EVENT_CONTAINER__LAST_ACTIVITY_ON__COLUMN_ID,
    AMARON__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID,
    AMARON__EVENT_CONTAINER__SOURCE__PRIORITY__COLUMN_ID,
    formatAmaronJourneyLastActivityDisplay as formatAmaronEventContainerLastActivityDisplay,
    resolveAmaronLastActivityOnDisplay,
    AmaronEnquiryStatusEnum,
    AmaronEventTypes,
    AmaronLeadStatusEnum,
    amaronEnquiryStatuses,
    amaronLeadStatuses,
} from "~/domainModels/workspaceSpecific/amaron";
import {
    EMOTORAD__EVENT_CONTAINER__ACTION_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_PLATFORM__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_CITY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_STATE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_ZONE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__STATUS__COLUMN_ID,
    EmotoradEventContainerStatusEnum,
    emotoradCurrentPlatforms,
    emotoradEventContainerStatuses,
} from "~/domainModels/workspaceSpecific/emotorad";
import {resolveEmotoradLifecycleDealerCityStateZone} from "~/utilities/resolveEmotoradLifecycleDealerCityStateZone";
import {NoteFeedItem} from "~/components/notes/noteFeedItem";
import {ListNotesPanelBody} from "~/components/notes/listNotesSidePanel";
import {useReportPanelDirty} from "~/components/ottopilotDataViewer/rowEngagement/rowEngagementUnsavedChangesContext";
import {useUnsavedChangesGuard} from "~/hooks/useUnsavedChangesGuard";
import {collectEventContainerNoteListItems} from "~/domainModels/notes";
import {ActivityTimeline} from "~/modules/activityTimeline/ActivityTimeline";
import {uniqueUuid} from "@vesta/unique/unique";
import {getProductCatalogueDisplayName} from "~/utilities/productCatalogueDisplayName";
import {DAY_PLAN_FOCUS_NOTES_PREVIEW_LIMIT} from "~/utilities/dayPlan/dayPlanViewPanelTabIndexes";
import {formatAmaronSourcePriorityScoreForDisplay} from "~/utilities/workspaceSpecific/amaronPriority";
import {resolveGeographyAdditionalColumnValue} from "~/utilities/geographyValueResolver";

type ResolvedColumnType = "OttopilotEventContainer" | "OttopilotEvent";

function isEventContainerColumnType(columnType: ResolvedColumnType): boolean {
    return columnType === "OttopilotEventContainer";
}

function isEventColumnType(columnType: ResolvedColumnType): boolean {
    return columnType === "OttopilotEvent";
}

function unknownValueToDisplayString(value: unknown, fallback = "-"): string {
    if (value === null || value === undefined) {
        return fallback;
    }
    if (typeof value === "string") {
        return value;
    }
    if (typeof value === "number" || typeof value === "boolean" || typeof value === "bigint") {
        return `${value}`;
    }
    if (Array.isArray(value)) {
        return value.map((item) => unknownValueToDisplayString(item, "")).join(", ");
    }
    if (typeof value === "object") {
        try {
            return JSON.stringify(value);
        } catch {
            return fallback;
        }
    }
    return fallback;
}

type ResolvedColumnGroupSection = {
    kind: OttopilotEventContainerViewPanelSectionKind.columnGroup;
    id: Uuid;
    displayName: string;
    icon?: (props: NewCoreIconProps) => JSX.Element;
    iconColor?: NewIconProps["color"];
    iconBackgroundColor?: string;
    initialIsOpen: boolean;
    className: string;
    columns: Array<{
        id: Uuid;
        label: string;
        columnId: Uuid | string;
        isAdditionalColumn: boolean;
        columnType: ResolvedColumnType;
        eventTemplateId?: Uuid;
    }>;
};

type ResolvedEventContainerOverviewSection = {
    kind: OttopilotEventContainerViewPanelSectionKind.eventContainerOverview;
    id: Uuid;
    initialIsOpen: boolean;
    className: string;
};

type ResolvedViewPanelSection = ResolvedColumnGroupSection | ResolvedEventContainerOverviewSection;

export type EventContainerInQueueHeaderBadgeKind = "assigned" | "unassigned" | "live" | "converted" | "churned" | "archived" | "neutral";

export function eventContainerInQueueHeaderBadgeColors(kind: EventContainerInQueueHeaderBadgeKind): {
    border: string;
    background: string;
    color?: string;
} {
    switch (kind) {
        case "assigned":
        case "live":
            return {border: token("color.border.success"), background: token("color.background.accent.lime.subtlest")};
        case "unassigned":
            return {border: token("color.chart.yellow.bolder"), background: token("color.background.accent.yellow.subtlest")};
        case "converted":
            return {border: token("color.border.information"), background: token("color.background.accent.blue.subtlest")};
        case "churned":
            return {border: token("color.border.danger"), background: token("color.background.accent.red.subtlest")};
        case "archived":
            return {
                border: token("color.border.danger"),
                background: token("color.background.accent.red.subtlest"),
                color: token("color.text.danger"),
            };
        case "neutral":
        default:
            return {border: token("color.border"), background: token("color.background.neutral.subtle")};
    }
}

function emotoradLifecycleOverviewHeaderStringHasContent(value: unknown): boolean {
    if (value === null || value === undefined) {
        return false;
    }
    return unknownValueToDisplayString(value, "").trim() !== "";
}

/** Raw Emotorad eventContainer status from additional columns (configured status column or Emotorad constant). */
export function getEmotoradLifecycleOverviewHeaderRawStatus(
    eventContainer: {additionalColumnValues: Record<Uuid, unknown>},
    eventContainerTemplate: EventContainerTemplateConfiguration,
): unknown {
    const configuredId = eventContainerTemplate.configuration.statusColumnConfig?.columnId;
    if (configuredId !== undefined) {
        const fromConfig = eventContainer.additionalColumnValues[configuredId];
        if (emotoradLifecycleOverviewHeaderStringHasContent(fromConfig) === true) {
            return fromConfig;
        }
    }
    return eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__STATUS__COLUMN_ID];
}

export function resolveEmotoradLifecycleOverviewHeaderStatus(rawStatus: unknown): {
    statusText: string;
    statusBadgeKind: EventContainerInQueueHeaderBadgeKind;
} {
    let statusText = "-";
    let statusBadgeKind: EventContainerInQueueHeaderBadgeKind = "neutral";
    if (emotoradLifecycleOverviewHeaderStringHasContent(rawStatus) !== true) {
        return {statusText, statusBadgeKind};
    }
    const statusId = unknownValueToDisplayString(rawStatus, "").trim();
    const match = Object.values(emotoradEventContainerStatuses).find((entry) => String(entry.id) === statusId);
    if (match === undefined) {
        return {statusText, statusBadgeKind};
    }
    statusText = match.displayName;
    if (statusId === String(EmotoradEventContainerStatusEnum.LIVE)) {
        statusBadgeKind = "live";
    } else if (statusId === String(EmotoradEventContainerStatusEnum.CONVERTED)) {
        statusBadgeKind = "converted";
    } else if (statusId === String(EmotoradEventContainerStatusEnum.CHURNED)) {
        statusBadgeKind = "churned";
    } else if (statusId === String(EmotoradEventContainerStatusEnum.ARCHIVE)) {
        statusBadgeKind = "archived";
    } else {
        statusBadgeKind = "neutral";
    }
    return {statusText, statusBadgeKind};
}

export function resolveAmaronLeadsHeaderStatus(rawStatus: unknown): {
    statusText: string;
    statusBadgeKind: EventContainerInQueueHeaderBadgeKind;
} {
    let statusText = "Live";
    let statusBadgeKind: EventContainerInQueueHeaderBadgeKind = "live";
    if (emotoradLifecycleOverviewHeaderStringHasContent(rawStatus) !== true) {
        return {statusText, statusBadgeKind};
    }
    const statusId = unknownValueToDisplayString(rawStatus, "").trim();
    const match = Object.values(amaronLeadStatuses).find((entry) => String(entry.id) === statusId);
    if (match === undefined) {
        return {statusText: statusId, statusBadgeKind: "neutral"};
    }
    statusText = match.displayName;
    if (statusId === String(AmaronLeadStatusEnum.LIVE)) {
        statusBadgeKind = "live";
    } else if (statusId === String(AmaronLeadStatusEnum.CONVERTED)) {
        statusBadgeKind = "converted";
    } else if (statusId === String(AmaronLeadStatusEnum.CHURNED)) {
        statusBadgeKind = "churned";
    } else if (statusId === String(AmaronLeadStatusEnum.ARCHIVED)) {
        statusBadgeKind = "archived";
    } else {
        statusBadgeKind = "neutral";
    }
    return {statusText, statusBadgeKind};
}

export function getAmaronEnquiryEvent(eventContainer: EventContainerWithEvents) {
    return eventContainer.events.find((event) => String(event.templateId) === String(AmaronEventTypes.Enquiry));
}

export function resolveAmaronEnquiryHeaderStatus(rawStatus: unknown): {
    statusText: string;
    statusBadgeKind: EventContainerInQueueHeaderBadgeKind;
} {
    let statusText = "Open";
    let statusBadgeKind: EventContainerInQueueHeaderBadgeKind = "live";
    if (emotoradLifecycleOverviewHeaderStringHasContent(rawStatus) !== true) {
        return {statusText, statusBadgeKind};
    }
    const statusId = unknownValueToDisplayString(rawStatus, "").trim();
    const match = Object.values(amaronEnquiryStatuses).find((entry) => String(entry.id) === statusId);
    if (match === undefined) {
        return {statusText: statusId, statusBadgeKind: "neutral"};
    }
    statusText = match.displayName;
    if (statusId === String(AmaronEnquiryStatusEnum.ENGAGED)) {
        statusBadgeKind = "converted";
    } else if (statusId === String(AmaronEnquiryStatusEnum.NON_ENGAGED) || statusId === String(AmaronEnquiryStatusEnum.ARCHIVE)) {
        statusBadgeKind = "churned";
    } else if (statusId === String(AmaronEnquiryStatusEnum.DUPLICATE)) {
        statusBadgeKind = "neutral";
    } else {
        statusBadgeKind = "live";
    }
    return {statusText, statusBadgeKind};
}

export function isInQueueEventContainerAssignmentOwnerSet(ownerRaw: unknown): boolean {
    if (ownerRaw === null || ownerRaw === undefined) {
        return false;
    }
    if (typeof ownerRaw === "string" && ownerRaw.trim() === "") {
        return false;
    }
    return true;
}

export function resolveInQueueAssignmentLabelFromEventContainer(
    eventContainer: EventContainerWithEvents,
    assignmentOwnerColumnId: Uuid | null,
): "Assigned" | "Unassigned" {
    if (assignmentOwnerColumnId === null) {
        return "Unassigned";
    }
    const ownerRaw = eventContainer.additionalColumnValues[assignmentOwnerColumnId];
    return isInQueueEventContainerAssignmentOwnerSet(ownerRaw) ? "Assigned" : "Unassigned";
}

function emotoradLifecycleOverviewHeaderPlatformRawToDisplayName(raw: unknown): string | null {
    if (emotoradLifecycleOverviewHeaderStringHasContent(raw) !== true) {
        return null;
    }
    const id = unknownValueToDisplayString(raw, "").trim();
    for (const def of Object.values(emotoradCurrentPlatforms)) {
        if (id === String(def.id)) {
            return def.displayName;
        }
    }
    return null;
}

/** Resolve current platform label for Emotorad lifecycle header (display names + known column IDs). */
export function resolveEmotoradLifecycleOverviewHeaderPlatformDisplay(params: {
    platformFromDisplayNameLookup: string;
    additionalColumnValues: Record<Uuid, unknown>;
}): string {
    const mappedFromLookup = emotoradLifecycleOverviewHeaderPlatformRawToDisplayName(params.platformFromDisplayNameLookup);
    if (mappedFromLookup !== null) {
        return mappedFromLookup;
    }
    if (emotoradLifecycleOverviewHeaderStringHasContent(params.platformFromDisplayNameLookup) === true) {
        return params.platformFromDisplayNameLookup;
    }
    const actionRaw = params.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__ACTION_PLATFORM__COLUMN_ID];
    const fromAction = emotoradLifecycleOverviewHeaderPlatformRawToDisplayName(actionRaw);
    if (fromAction !== null) {
        return fromAction;
    }
    const customerRaw = params.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__CUSTOMER_PLATFORM__COLUMN_ID];
    const fromCustomer = emotoradLifecycleOverviewHeaderPlatformRawToDisplayName(customerRaw);
    if (fromCustomer !== null) {
        return fromCustomer;
    }
    return "-";
}

// ─── Config → resolved sections ─────────────────────────────────────────────

function getViewPanelSectionsFromConfig(getAdditionalColumns: CapabilityUdoColumnResolver, config: EventContainerTemplateConfiguration): Array<ResolvedViewPanelSection> {
    const viewPanel = config.configuration.eventContainersPage.viewPanel;
    const baseSectionClassName = "py-ds-space-100 px-ds-space-200";
    const firstSectionClassName = `border-t border-ds-border ${baseSectionClassName}`;
    const nextSectionClassName = `border-t border-ds-border ${baseSectionClassName}`;

    if (viewPanel === undefined) {
        return [
            {
                kind: OttopilotEventContainerViewPanelSectionKind.columnGroup,
                id: "fallback-eventContainer-details" as Uuid,
                displayName: "Journey details",
                icon: InformationCircleIcon,
                iconColor: "var(--ds-icon-accent-blue)" as NewIconProps["color"],
                iconBackgroundColor: "var(--ds-background-accent-blue-subtlest)",
                initialIsOpen: true,
                className: firstSectionClassName,
                columns: getAdditionalColumns(config).map((col) => ({
                    id: col.id,
                    label: col.displayName,
                    columnId: col.id,
                    isAdditionalColumn: true,
                    columnType: "OttopilotEventContainer" as const,
                })),
            },
            {
                kind: OttopilotEventContainerViewPanelSectionKind.eventContainerOverview,
                id: "fallback-eventContainer-overview" as Uuid,
                initialIsOpen: false,
                className: nextSectionClassName,
            },
            {
                kind: OttopilotEventContainerViewPanelSectionKind.columnGroup,
                id: "fallback-timeline" as Uuid,
                displayName: "Timeline",
                icon: SubtasksIcon,
                iconColor: "var(--ds-icon-accent-purple)" as NewIconProps["color"],
                iconBackgroundColor: "var(--ds-background-accent-purple-subtlest)",
                initialIsOpen: false,
                className: nextSectionClassName,
                columns: [
                    {
                        id: "fallback-created-at" as Uuid,
                        label: "Created on",
                        columnId: "createdAt",
                        isAdditionalColumn: false,
                        columnType: "OttopilotEventContainer" as const,
                    },
                    {
                        id: "fallback-updated-at" as Uuid,
                        label: "Last modified on",
                        columnId: "updatedAt",
                        isAdditionalColumn: false,
                        columnType: "OttopilotEventContainer" as const,
                    },
                ],
            },
            {
                kind: OttopilotEventContainerViewPanelSectionKind.columnGroup,
                id: "fallback-eventContainer-metadata" as Uuid,
                displayName: "Journey metadata",
                icon: PersonAvatarIcon,
                iconColor: "var(--ds-icon-accent-red)" as NewIconProps["color"],
                iconBackgroundColor: "var(--ds-background-accent-red-subtlest)",
                initialIsOpen: false,
                className: nextSectionClassName,
                columns: [
                    {
                        id: "fallback-created-by" as Uuid,
                        label: "Created by",
                        columnId: "createdBy",
                        isAdditionalColumn: false,
                        columnType: "OttopilotEventContainer" as const,
                    },
                    {
                        id: "fallback-updated-by" as Uuid,
                        label: "Last modified by",
                        columnId: "updatedBy",
                        isAdditionalColumn: false,
                        columnType: "OttopilotEventContainer" as const,
                    },
                ],
            },
        ];
    }

    return viewPanel.sections.map((section, index) => {
        const className = section.className ?? (index === 0 ? firstSectionClassName : nextSectionClassName);
        if (section.kind === OttopilotEventContainerViewPanelSectionKind.eventContainerOverview) {
            return {
                kind: OttopilotEventContainerViewPanelSectionKind.eventContainerOverview,
                id: section.id,
                initialIsOpen: section.initialIsOpen ?? true,
                className,
            };
        }

        // columnGroup
        const icon = section.iconId !== undefined ? eventContainerEditPanelIconsMap[section.iconId] : undefined;
        return {
            kind: OttopilotEventContainerViewPanelSectionKind.columnGroup,
            id: section.id,
            displayName: section.displayName,
            icon,
            iconColor: section.iconColor as NewIconProps["color"] | undefined,
            iconBackgroundColor: section.iconBackgroundColor,
            initialIsOpen: section.initialIsOpen ?? true,
            className,
            columns: section.columns.map((col) => {
                const columnType = col.columnType as ResolvedColumnType;
                return {
                    id: col.id,
                    label: col.label,
                    columnId: col.columnId,
                    isAdditionalColumn: col.isAdditionalColumn,
                    columnType,
                    eventTemplateId: isEventColumnType(columnType) === true ? col.eventTemplateId : undefined,
                };
            }),
        };
    });
}

// ─── Column value resolution ─────────────────────────────────────────────────

type ColumnValueDeps = {
    row: EventContainerWithEvents;
    config: EventContainerTemplateConfiguration;
    eventTemplates: Array<OttopilotEventTemplate>;
    users: ReturnType<typeof useUsers>;
    userTemplates: ReturnType<typeof useUserTemplates>;
    platformUserDetailsByUserId: ReadonlyMap<Uuid, ReturnType<typeof useWorkspace>["platformUserDetails"][number]>;
    organizationalUnits: ReturnType<typeof useOrganizationalUnits>;
    organizationalUnitTemplates: ReturnType<typeof useOrganizationalUnitTemplates>;
    products: ReturnType<typeof useProductCatalogue>;
    productCatalogueTemplates: ReturnType<typeof useProductCatalogueTemplates>;
    userDefinedObjectOptionsByTemplateId: ReturnType<typeof useWorkspace>["userDefinedObjectOptionsByTemplateId"];
};

const USER_REFERENCE_COLUMN_LABELS = new Set([
    "assigned to",
    "assigned agent",
    "assignee",
    "lead owner",
    "lifecycle owner",
    "event owner",
    "created by",
    "modified by",
    "last modified by",
]);

function resolveColumnValue(getAdditionalColumns: CapabilityUdoColumnResolver, col: ResolvedColumnGroupSection["columns"][number], deps: ColumnValueDeps, isInQueueStylePanel: boolean): string {
    const {
        row,
        config,
        eventTemplates,
        users,
        userTemplates,
        platformUserDetailsByUserId,
        organizationalUnits,
        organizationalUnitTemplates,
        products,
        productCatalogueTemplates,
        userDefinedObjectOptionsByTemplateId,
    } = deps;

    // System field
    if (col.isAdditionalColumn === false) {
        if (isEventContainerColumnType(col.columnType) === true) {
            switch (col.columnId) {
                case "createdAt":
                    return formatDateTime(row.createdAt);
                case "updatedAt":
                    return formatDateTime(row.updatedAt);
                case "createdBy":
                    return getUserDisplayName(row.createdBy, users, userTemplates, platformUserDetailsByUserId);
                case "updatedBy":
                    return getUserDisplayName(row.updatedBy, users, userTemplates, platformUserDetailsByUserId);
                default:
                    return unknownValueToDisplayString((row as Record<string, unknown>)[String(col.columnId)]);
            }
        } else {
            // Event system field
            const event = row.events.find((e) => e.templateId === col.eventTemplateId);
            if (event === undefined) {
                return "-";
            }
            switch (col.columnId) {
                case "createdAt":
                    return formatDateTime((event as Record<string, unknown>)["createdAt"] as string);
                case "updatedAt":
                    return formatDateTime((event as Record<string, unknown>)["updatedAt"] as string);
                case "createdBy":
                    return getUserDisplayName((event as Record<string, unknown>)["createdBy"] as Uuid, users, userTemplates, platformUserDetailsByUserId);
                case "updatedBy":
                    return getUserDisplayName((event as Record<string, unknown>)["updatedBy"] as Uuid, users, userTemplates, platformUserDetailsByUserId);
                default:
                    return unknownValueToDisplayString((event as Record<string, unknown>)[String(col.columnId)]);
            }
        }
    }

    // Emotorad Lifecycle Overview table resolves dealer city/state/zone from the assigned dealer OU tree.
    if (isEventContainerColumnType(col.columnType) === true) {
        const geographyKind =
            col.columnId === EMOTORAD__EVENT_CONTAINER__DEALER_CITY__COLUMN_ID
                ? ("city" as const)
                : col.columnId === EMOTORAD__EVENT_CONTAINER__DEALER_STATE__COLUMN_ID
                  ? ("state" as const)
                  : col.columnId === EMOTORAD__EVENT_CONTAINER__DEALER_ZONE__COLUMN_ID
                    ? ("zone" as const)
                    : null;
        if (geographyKind !== null) {
            return resolveEmotoradLifecycleDealerCityStateZone(row, config, geographyKind, organizationalUnits, organizationalUnitTemplates);
        }
    }

    // Additional column
    let rawValue: unknown;
    if (isEventContainerColumnType(col.columnType) === true) {
        rawValue = row.additionalColumnValues[col.columnId as Uuid];
    } else {
        const event = row.events.find((e) => e.templateId === col.eventTemplateId);
        rawValue = event?.additionalColumnValues[col.columnId as Uuid];
    }

    const resolveUserReference = (value: unknown): string | null => {
        if (typeof value !== "string" || value.trim() === "") {
            return null;
        }
        const displayName = getUserDisplayName(value.trim() as Uuid, users, userTemplates, platformUserDetailsByUserId);
        return displayName === "-" ? null : displayName;
    };
    const normalizedLabel = col.label.trim().toLowerCase();
    const isKnownUserReferenceLabel = USER_REFERENCE_COLUMN_LABELS.has(normalizedLabel);
    if (isKnownUserReferenceLabel === true) {
        const displayName = resolveUserReference(rawValue);
        if (displayName !== null) {
            return displayName;
        }
    }

    if (isEventColumnType(col.columnType) === true) {
        const eventTemplate = eventTemplates.find((template) => template.id === col.eventTemplateId);
        const eventColumnDefinition = getAdditionalColumns(eventTemplate).find((column) => column.id === col.columnId);
        if (eventColumnDefinition?.type === UdoColumnType.user) {
            return resolveUserReference(rawValue) ?? "-";
        }
    }

    const colDef = getAdditionalColumns(config).find((c) => c.id === col.columnId);
    if (colDef === undefined) {
        return unknownValueToDisplayString(rawValue);
    }

    if (String(col.columnId) === String(AMARON__EVENT_CONTAINER__SOURCE__PRIORITY__COLUMN_ID)) {
        const score = typeof rawValue === "number" ? rawValue : Number(rawValue);
        if (Number.isFinite(score) === true) {
            const formatted = formatAmaronSourcePriorityScoreForDisplay(score);
            if (formatted !== "") {
                return formatted;
            }
        }
        return "-";
    }

    if (isEventContainerColumnType(col.columnType) === true) {
        if (String(col.columnId) === String(AMARON__EVENT_CONTAINER__LAST_ACTIVITY__COLUMN_ID)) {
            const activityValue = unknownValueToDisplayString(rawValue, "");
            return formatAmaronEventContainerLastActivityDisplay(activityValue, eventTemplates);
        }
        if (String(col.columnId) === String(AMARON__EVENT_CONTAINER__LAST_ACTIVITY_ON__COLUMN_ID)) {
            return resolveAmaronLastActivityOnDisplay(row, rawValue);
        }
    }

    // emotorad workaround: Assigned Dealer may be stored as an OU UUID even when the column type is not `ou`.
    // Render the OU display name to avoid showing the raw UUID in the UI.
    if (isInQueueStylePanel === true && colDef.id === EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID && typeof rawValue === "string") {
        const ou = organizationalUnits.find((u) => u.id === rawValue);
        if (ou !== undefined) {
            const ouTemplate = organizationalUnitTemplates.find((t) => t.id === ou.templateId);
            if (ouTemplate !== undefined) {
                return processOttopilotStringTemplate(ouTemplate.instanceDisplayNameTemplate, ou.additionalColumnValues);
            }
        }
    }

    return formatUserDefinedObjectAdditionalColumnValue(colDef, rawValue, {
        formatTimestamp: formatDateTime,
        referenceOptionsByTemplateId: userDefinedObjectOptionsByTemplateId,
        resolveUser: id => getUserDisplayName(id, users, userTemplates, platformUserDetailsByUserId),
        resolveUserTemplate: id => userTemplates.find(template => template.id === id)?.displayName ?? "?",
        resolveGeography: resolveGeographyAdditionalColumnValue,
        resolveOrganizationalUnit: id => {
            const unit = organizationalUnits.find(item => item.id === id);
            const template = organizationalUnitTemplates.find(item => item.id === unit?.templateId);
            return unit !== undefined && template !== undefined ? processOttopilotStringTemplate(template.instanceDisplayNameTemplate, unit.additionalColumnValues) : "-";
        },
        resolveProduct: id => {
            const product = products?.find(item => item.id === id);
            return product !== undefined ? getProductCatalogueDisplayName(product, productCatalogueTemplates) : "-";
        },
    });
}

// ─── Truncated field value (click to expand full content) ────────────────────

/**
 * Shows the value on a single line, truncating with an ellipsis when it overflows
 * the available width. Tapping/clicking a truncated value expands it to render the
 * full content (wraps to multiple lines). Tapping again collapses it back.
 */
function TruncatedFieldValue({value}: {value: string}) {
    const textRef = useRef<HTMLSpanElement>(null);
    const [isExpanded, setIsExpanded] = useState(false);

    const handleClick = () => {
        if (isExpanded) {
            setIsExpanded(false);
            return;
        }
        const el = textRef.current;
        if (el !== null && el.scrollWidth > el.clientWidth + 1) {
            setIsExpanded(true);
        }
    };

    return (
        <div className="min-w-0 w-full">
            <span
                ref={textRef}
                onClick={handleClick}
                className="text-ds-font-body !font-ds-font-weight-regular text-ds-text cursor-pointer block max-w-full"
                style={
                    isExpanded
                        ? {whiteSpace: "normal", wordBreak: "break-word"}
                        : {
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                          }
                }
            >
                {value}
            </span>
        </div>
    );
}

// ─── Phone number field (with copy + WhatsApp) ────────────────────────────────

function PhoneNumberField({
    label,
    phoneNumber,
    showCopy = true,
    showWhatsApp = true,
}: {
    label: string;
    phoneNumber: string;
    showCopy?: boolean;
    showWhatsApp?: boolean;
}) {
    const [, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(phoneNumber);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            console.error("Failed to copy text:", error);
        }
    };

    const whatsappNumber = phoneNumber.replace(/[^0-9]/g, "");

    return (
        <div className="grid grid-rows-[max-content_max-content] gap-y-ds-space-050">
            <AnankeLabel>{label}</AnankeLabel>
            {phoneNumber !== "" ? (
                <div className="flex w-fit max-w-full flex-wrap items-center gap-x-ds-space-100 gap-y-ds-space-050">
                    <AnankeText>{phoneNumber}</AnankeText>
                    <div className="grid grid-flow-col gap-ds-space-050 items-center">
                        {showCopy === true ? (
                            <AnankeIconButton
                                type="button"
                                icon={(props) => <CopyIcon {...props} />}
                                label="Copy"
                                appearance="subtle"
                                onClick={handleCopy}
                            />
                        ) : null}
                        {showWhatsApp === true ? (
                            <AnankeLinkIconButton
                                icon={() => <WhatsappIcon />}
                                label="WhatsApp"
                                appearance="subtle"
                                href={`https://wa.me/${whatsappNumber}`}
                                isDisabled={whatsappNumber === ""}
                            />
                        ) : null}
                    </div>
                </div>
            ) : (
                <AnankeText>-</AnankeText>
            )}
        </div>
    );
}

function resolveFieldGridColumnCount(fieldGridColumnCount: number | undefined, useTwoColumnFieldGrid: boolean): number {
    if (fieldGridColumnCount !== undefined) {
        return fieldGridColumnCount;
    }
    return useTwoColumnFieldGrid === true ? 2 : 1;
}

function fieldGridContainerClassName(columnCount: number): string {
    if (columnCount === 3) {
        return "grid grid-cols-2 sm:grid-cols-3 gap-x-ds-space-200 gap-y-ds-space-200";
    }
    if (columnCount === 2) {
        return "grid grid-cols-2 gap-x-ds-space-200 gap-y-ds-space-200";
    }
    return "grid grid-flow-row gap-y-ds-space-200";
}

function fieldGridFullRowSpanClassName(columnCount: number): string {
    if (columnCount === 3) {
        return "col-span-2 sm:col-span-3";
    }
    if (columnCount === 2) {
        return "col-span-2";
    }
    return "";
}

// ─── Journey overview section ─────────────────────────────────────────────────

function EventContainerOverviewCollapsibleSection({
    eventTypeCounts,
    initialIsOpen,
    className,
}: {
    eventTypeCounts: Array<{template: OttopilotEventTemplate; count: number}>;
    initialIsOpen: boolean;
    className?: string;
}) {
    const eventRows: Array<Array<{template: OttopilotEventTemplate; count: number}>> = [];
    for (let i = 0; i < eventTypeCounts.length; i += 2) {
        eventRows.push(eventTypeCounts.slice(i, i + 2));
    }

    return (
        <AnankeTempCollapsibleSectionWithIcon
            title="Journey overview"
            icon={TagIcon}
            iconColor="var(--ds-icon-accent-blue)"
            iconBackgroundColor="var(--ds-background-accent-blue-subtlest)"
            initialIsOpen={initialIsOpen}
            className={className}
        >
            <div className="grid grid-flow-row gap-y-ds-space-150">
                <ItemBuilder
                    items={eventRows}
                    itemBuilder={(row) => (
                        <div
                            key={row.map((item) => item.template.id).join("-")}
                            className="grid grid-cols-2 gap-ds-space-150"
                        >
                            <ItemBuilder
                                items={row}
                                itemBuilder={(item) => (
                                    <div
                                        key={item.template.id}
                                        className="bg-ds-background-disabled flex gap-ds-space-075 items-center px-ds-space-150 py-ds-space-100 rounded-ds-border-radius"
                                    >
                                        <div className="flex-1 min-w-0">
                                            <span className="truncate">
                                                <AnankeText size="medium">{item.template.displayName}</AnankeText>
                                            </span>
                                        </div>
                                        <div className="bg-ds-background-neutral-subtle flex items-center px-ds-space-050 rounded-ds-border-radius-small">
                                            <span className="font-bold uppercase">
                                                <AnankeText size="small">{item.count.toString()}</AnankeText>
                                            </span>
                                        </div>
                                    </div>
                                )}
                            />
                        </div>
                    )}
                />
            </div>
        </AnankeTempCollapsibleSectionWithIcon>
    );
}

// ─── Column group section ─────────────────────────────────────────────────────

function CollapsibleSectionLoadingSkeleton({fieldGridColumnCount}: {fieldGridColumnCount: number}) {
    const skeletonFieldCount = fieldGridColumnCount * 3;

    return (
        <div className={fieldGridContainerClassName(fieldGridColumnCount)}>
            {Array.from({length: skeletonFieldCount}, (_, index) => (
                <div
                    key={index}
                    className="grid min-w-0 grid-rows-[max-content_max-content] gap-y-ds-space-050"
                >
                    <AnankeSkeleton
                        width="55%"
                        height={12}
                    />
                    <AnankeSkeleton
                        width="85%"
                        height={16}
                    />
                </div>
            ))}
        </div>
    );
}

function ColumnGroupSection({
    section,
    deps,
    fieldGridColumnCount,
    isInQueueStylePanel,
    useCompactPhoneField = false,
    isSectionContentLoading = false,
}: {
    section: ResolvedColumnGroupSection;
    deps: ColumnValueDeps;
    fieldGridColumnCount: number;
    isInQueueStylePanel: boolean;
    useCompactPhoneField?: boolean;
    isSectionContentLoading?: boolean;
}) {
    const getAdditionalColumns = useCapabilityUdoColumns();
    const {config} = deps;

    const sectionBody =
        isSectionContentLoading === true ? (
            <CollapsibleSectionLoadingSkeleton fieldGridColumnCount={fieldGridColumnCount} />
        ) : (
            <div className={fieldGridContainerClassName(fieldGridColumnCount)}>
                {section.columns.map((col) => {
                    if (col.isAdditionalColumn) {
                        const colDef = getAdditionalColumns(config).find((c) => c.id === col.columnId);
                        if (colDef?.type === UdoColumnType.indianPhoneNumber) {
                            const phoneValue = resolveColumnValue(getAdditionalColumns, col, deps, isInQueueStylePanel);
                            const spanFullRow = fieldGridColumnCount > 1 && useCompactPhoneField !== true;
                            return (
                                <div
                                    key={col.id}
                                    className={spanFullRow === true ? fieldGridFullRowSpanClassName(fieldGridColumnCount) : "min-w-0"}
                                >
                                    <PhoneNumberField
                                        label={col.label}
                                        phoneNumber={phoneValue}
                                        showCopy={true}
                                        showWhatsApp={useCompactPhoneField !== true}
                                    />
                                </div>
                            );
                        }
                    }

                    const displayValue = resolveColumnValue(getAdditionalColumns, col, deps, isInQueueStylePanel);
                    return (
                        <div
                            key={col.id}
                            className="grid grid-rows-[max-content_max-content] gap-y-ds-space-050 min-w-0"
                        >
                            <AnankeLabel>{col.label}</AnankeLabel>
                            <TruncatedFieldValue value={displayValue} />
                        </div>
                    );
                })}
            </div>
        );

    return renderColumnGroupCollapsibleSection({
        section: {
            ...section,
            initialIsOpen: isSectionContentLoading === true ? true : section.initialIsOpen,
        },
        children: sectionBody,
    });
}

function renderColumnGroupCollapsibleSection({section, children}: {section: ResolvedColumnGroupSection; children: ReactNode}) {
    if (section.icon !== undefined && section.iconColor !== undefined && section.iconBackgroundColor !== undefined) {
        return (
            <AnankeTempCollapsibleSectionWithIcon
                title={section.displayName}
                icon={section.icon}
                iconColor={section.iconColor}
                iconBackgroundColor={section.iconBackgroundColor}
                initialIsOpen={section.initialIsOpen}
                className={section.className}
            >
                {children}
            </AnankeTempCollapsibleSectionWithIcon>
        );
    }

    return (
        <AnankeTempFormCollapsibleSection
            title={section.displayName}
            initialIsOpen={section.initialIsOpen}
            className={section.className}
        >
            {children}
        </AnankeTempFormCollapsibleSection>
    );
}

function FocusInlineColumnGroupLoadingSection({section, fieldGridColumnCount}: {section: ResolvedColumnGroupSection; fieldGridColumnCount: number}) {
    return renderColumnGroupCollapsibleSection({
        section: {
            ...section,
            initialIsOpen: true,
        },
        children: <CollapsibleSectionLoadingSkeleton fieldGridColumnCount={fieldGridColumnCount} />,
    });
}

export function EventContainerViewPanelFocusInlineLoadingShell({
    eventContainerTemplate,
    fieldGridColumnCount = 2,
}: {
    eventContainerTemplate: EventContainerTemplateConfiguration;
    fieldGridColumnCount?: number;
}) {
    const getAdditionalColumns = useCapabilityUdoColumns();
    const sections = getViewPanelSectionsFromConfig(getAdditionalColumns, eventContainerTemplate);
    const detailSections = sections.filter((section) => section.kind === OttopilotEventContainerViewPanelSectionKind.columnGroup);
    const resolvedFieldGridColumnCount = resolveFieldGridColumnCount(fieldGridColumnCount, false);

    return (
        <div className="w-full min-w-0 grid grid-flow-row">
            {detailSections.map((section) => (
                <FocusInlineColumnGroupLoadingSection
                    key={section.id}
                    section={section}
                    fieldGridColumnCount={resolvedFieldGridColumnCount}
                />
            ))}
            <AnankeTempCollapsibleSectionWithIcon
                title="Recent Notes"
                icon={NoteIcon}
                iconColor="var(--ds-icon-accent-blue)"
                iconBackgroundColor={token("color.background.accent.teal.subtlest")}
                initialIsOpen={true}
                className="border-t border-ds-border py-ds-space-100 px-ds-space-200"
            >
                <CollapsibleSectionLoadingSkeleton fieldGridColumnCount={resolvedFieldGridColumnCount} />
            </AnankeTempCollapsibleSectionWithIcon>
        </div>
    );
}

function ViewPanelTabPanelContent({children}: {children: ReactNode}) {
    return <div className="w-full min-w-0 flex-1">{children}</div>;
}

function EventContainerViewPanelNotesContent({noteItems}: {noteItems: ReturnType<typeof collectEventContainerNoteListItems>}) {
    return (
        <div className="grid gap-ds-space-150 py-ds-space-100 px-ds-space-200">
            {noteItems.length === 0 ? (
                <AnankeText color="color.text.subtlest">No notes yet.</AnankeText>
            ) : (
                noteItems.map((item) => (
                    <NoteFeedItem
                        key={item.id}
                        item={item}
                        canMutate={false}
                        isEditing={false}
                        onStartEdit={() => undefined}
                        onCancelEdit={() => undefined}
                        onDelete={() => undefined}
                        truncateEnabled={true}
                        editComposer={null}
                    />
                ))
            )}
        </div>
    );
}

// ─── Main exported component ──────────────────────────────────────────────────

export function EventContainerViewPanel({
    row,
    isExpanded,
    eventContainerTemplate,
    eventTemplates,
    notesEventContainerSourceLabel,
    useTwoColumnFieldGrid = false,
    fieldGridColumnCount,
    useInQueueHeader = false,
    useEmotoradLifecycleOverviewStatusHeader = false,
    useAmaronLeadsStatusHeader = false,
    useAmaronEnquiryStatusHeader = false,
    showEventContainerProfileLink = true,
    hideAssigneeBar = false,
    usePanelTabs = false,
    initialPanelTabIndex = 0,
    layout = "default",
    resolveInQueueAssignmentLabel,
    inQueueAssignmentOwnerColumnId = null,
    isSectionContentLoading = false,
    onViewAllNotes,
    onEditClick,
}: {
    row: EventContainerWithEvents;
    isExpanded: boolean;
    eventContainerTemplate: EventContainerTemplateConfiguration;
    eventTemplates: Array<OttopilotEventTemplate>;
    notesEventContainerSourceLabel?: string;
    useTwoColumnFieldGrid?: boolean;
    /** When set, overrides `useTwoColumnFieldGrid` (e.g. 2 in sidebar, 3 when expanded). */
    fieldGridColumnCount?: number;
    useInQueueHeader?: boolean;
    /** Emotorad Lifecycle Overview: header chip shows eventContainer status (Live / Converted / Churned) only. */
    useEmotoradLifecycleOverviewStatusHeader?: boolean;
    /** Amaron Leads: header chip shows lead status (Live / Converted / Churned / Archived). */
    useAmaronLeadsStatusHeader?: boolean;
    /** Amaron Day Plan: header chip shows enquiry status (Open / Engaged / Non-Engaged). */
    useAmaronEnquiryStatusHeader?: boolean;
    /** When false, hides the external-link control that opens the full eventContainer profile page. */
    showEventContainerProfileLink?: boolean;
    /** Hides the assignee strip below the in-queue style header (My Day Plan). */
    hideAssigneeBar?: boolean;
    /** Details / Activity / Notes tabs below the header (My Day Plan). */
    usePanelTabs?: boolean;
    /** Initial tab when `usePanelTabs` is true (0 = Details, 1 = Activity, 2 = Notes). */
    initialPanelTabIndex?: number;
    /** Inline collapsible sections for My Day Plan Focus view (no header/tabs). */
    layout?: "default" | "focusInline";
    /** When true, collapsible section bodies show a loading spinner instead of field values. */
    isSectionContentLoading?: boolean;
    /** Focus inline: opens the profile Notes tab with the full notes list. */
    onViewAllNotes?: () => void;
    /** Agent view (My Day Plan / Home): edit control next to the customer name; opens the edit panel. */
    onEditClick?: () => void;
    /** In Queue assignment chip; must match table/kanban (lead owner column, not AON). */
    resolveInQueueAssignmentLabel?: (eventContainer: EventContainerWithEvents) => "Assigned" | "Unassigned";
    inQueueAssignmentOwnerColumnId?: Uuid | null;
}) {
    const getAdditionalColumns = useCapabilityUdoColumns();
    const resolvedNotesEventContainerSourceLabel =
        notesEventContainerSourceLabel ?? eventContainerTemplate.configuration.moduleDisplayNameVariants.singularSentenceCase;
    const users = useUsers();
    const userTemplates = useUserTemplates();
    const organizationalUnits = useOrganizationalUnits();
    const organizationalUnitTemplates = useOrganizationalUnitTemplates();
    const products = useProductCatalogue();
    const productCatalogueTemplates = useProductCatalogueTemplates();
    const {workspace, platformUserDetails, userDefinedObjectOptionsByTemplateId} = useWorkspace();
    const navigate = useNavigate();
    const mountedAtRef = useRef(Date.now());
    const [panelTabIndex, setPanelTabIndex] = useState(initialPanelTabIndex);
    const [eventContainerForNotes, setEventContainerForNotes] = useState(row);
    const [notesDirty, setNotesDirty] = useState(false);
    const [notesComposerKey, setNotesComposerKey] = useState(0);
    const pendingPanelTabIndexRef = useRef<number | null>(null);

    useReportPanelDirty(notesDirty);

    const {requestClose: requestNotesTabLeave, modal: notesTabLeaveModal} = useUnsavedChangesGuard({
        isDirty: notesDirty,
        onDiscard: () => {
            setNotesDirty(false);
            setNotesComposerKey((current) => current + 1);
            const pendingTabIndex = pendingPanelTabIndexRef.current;
            pendingPanelTabIndexRef.current = null;
            if (pendingTabIndex !== null) {
                setPanelTabIndex(pendingTabIndex);
            }
        },
    });
    const resolvedFieldGridColumnCount = resolveFieldGridColumnCount(fieldGridColumnCount, useTwoColumnFieldGrid);
    const platformUserDetailsByUserId = useMemo(
        () => new Map(platformUserDetails.map((platformUserDetail) => [platformUserDetail.userId, platformUserDetail])),
        [platformUserDetails],
    );

    useEffect(() => {
        setEventContainerForNotes(row);
    }, [row]);

    useEffect(() => {
        setPanelTabIndex(initialPanelTabIndex);
    }, [initialPanelTabIndex, row.id]);

    const noteItems = collectEventContainerNoteListItems({
        eventContainer: row,
        eventTemplates,
        resolveAuthor: (userId) => {
            const user = users.find((item) => item.id === userId);
            const templateId = user?.templateId ?? null;
            return {
                displayName: getUserDisplayName(userId, users, userTemplates, platformUserDetailsByUserId),
                roleLabel: templateId !== null ? getUserRole(templateId, userTemplates) : "",
            };
        },
        eventContainerSourceLabel: resolvedNotesEventContainerSourceLabel,
        sortDirection: "recent",
    });

    const navigateToEventContainerDetails = (event: MouseEvent) => {
        event.stopPropagation();
        if (Date.now() - mountedAtRef.current < 500) {
            return;
        }
        navigate({to: buildJourneyProfilePath({workspaceId: workspace.id, eventContainerId: row.id, eventContainerTemplateId: row.templateId})});
    };

    const sections = useMemo(() => getViewPanelSectionsFromConfig(getAdditionalColumns, eventContainerTemplate), [getAdditionalColumns, eventContainerTemplate]);

    const eventCounts = useMemo(() => {
        const counts = new Map<Uuid, number>();
        for (const event of row.events) {
            const currentCount = counts.get(event.templateId) ?? 0;
            counts.set(event.templateId, currentCount + 1);
        }
        return counts;
    }, [row.events]);

    const eventTypeCounts = useMemo(
        () => eventTemplates.map((template) => ({template, count: eventCounts.get(template.id) ?? 0})),
        [eventTemplates, eventCounts],
    );

    const deps: ColumnValueDeps = {
        row,
        config: eventContainerTemplate,
        eventTemplates,
        users,
        userTemplates,
        platformUserDetailsByUserId,
        organizationalUnits,
        organizationalUnitTemplates,
        products,
        productCatalogueTemplates,
        userDefinedObjectOptionsByTemplateId,
    };

    const renderSection = (section: ResolvedViewPanelSection) => {
        if (section.kind === OttopilotEventContainerViewPanelSectionKind.eventContainerOverview) {
            return (
                <EventContainerOverviewCollapsibleSection
                    key={section.id}
                    eventTypeCounts={eventTypeCounts}
                    initialIsOpen={section.initialIsOpen}
                    className={section.className}
                />
            );
        }
        return (
            <ColumnGroupSection
                key={section.id}
                section={section}
                deps={deps}
                fieldGridColumnCount={resolvedFieldGridColumnCount}
                isInQueueStylePanel={useInQueueHeader === true}
                useCompactPhoneField={usePanelTabs === true}
                isSectionContentLoading={isSectionContentLoading}
            />
        );
    };

    const inQueueHeaderData = useMemo(() => {
        if (useInQueueHeader !== true) {
            return null;
        }

        const byDisplayName = new Map(getAdditionalColumns(eventContainerTemplate).map((column) => [column.displayName.trim().toLowerCase(), column.id]));
        const resolveUserDisplayName = (value: unknown): string | null => {
            if (typeof value !== "string") {
                return null;
            }
            const rawValue = value.trim();
            if (rawValue === "") {
                return null;
            }
            const displayName = getUserDisplayName(rawValue as Uuid, users, userTemplates, platformUserDetailsByUserId);
            return displayName === "-" ? null : displayName;
        };
        const getAdditionalValue = (...displayNames: string[]) => {
            for (const displayName of displayNames) {
                const id = byDisplayName.get(displayName.trim().toLowerCase());
                if (id !== undefined) {
                    const value = row.additionalColumnValues[id];
                    const displayValue = unknownValueToDisplayString(value, "");
                    if (displayValue.trim() !== "") {
                        return resolveUserDisplayName(value) ?? displayValue;
                    }
                }
            }
            return "";
        };

        const assignmentOwnerDisplay = getAdditionalValue("Lifecycle Owner", "Lead Owner", "Assigned To", "Assigned Agent");
        const resolveAssigneeNameFromOwnerColumn = (): string => {
            if (inQueueAssignmentOwnerColumnId === null) {
                return assignmentOwnerDisplay === "" ? "-" : assignmentOwnerDisplay;
            }
            const ownerRaw = row.additionalColumnValues[inQueueAssignmentOwnerColumnId];
            if (isInQueueEventContainerAssignmentOwnerSet(ownerRaw) !== true) {
                return "-";
            }
            const matchingColumn = getAdditionalColumns(eventContainerTemplate).find((column) => column.id === inQueueAssignmentOwnerColumnId);
            if (matchingColumn?.type === UdoColumnType.user) {
                return resolveUserDisplayName(ownerRaw) ?? "-";
            }
            return resolveUserDisplayName(ownerRaw) ?? unknownValueToDisplayString(ownerRaw);
        };
        const customerName = getAdditionalValue("Customer Name", "customer name", "User Name", "user name") || "-";
        const platform = getAdditionalValue("Action Platform", "Platform", "Utm_platform", "utm_platform");

        if (useAmaronEnquiryStatusHeader === true) {
            const enquiryEvent = getAmaronEnquiryEvent(row);
            const rawStatus = enquiryEvent?.additionalColumnValues[AMARON__EVENT__ENQUIRY__ENQUIRY_STATUS__COLUMN_ID];
            const {statusText, statusBadgeKind} = resolveAmaronEnquiryHeaderStatus(rawStatus);
            const platformDisplay = platform === "" ? "-" : platform;
            return {
                statusText,
                statusBadgeKind,
                customerName,
                platform: platformDisplay,
                assigneeName: assignmentOwnerDisplay === "" ? "-" : assignmentOwnerDisplay,
                dateText: formatDateTime(row.createdAt),
            };
        }

        if (useAmaronLeadsStatusHeader === true) {
            const rawStatus = row.additionalColumnValues[AMARON__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID];
            const {statusText, statusBadgeKind} = resolveAmaronLeadsHeaderStatus(rawStatus);
            const platformDisplay = platform === "" ? "-" : platform;
            return {
                statusText,
                statusBadgeKind,
                customerName,
                platform: platformDisplay,
                assigneeName: assignmentOwnerDisplay === "" ? "-" : assignmentOwnerDisplay,
                dateText: formatDateTime(row.createdAt),
            };
        }

        if (useEmotoradLifecycleOverviewStatusHeader === true) {
            const rawStatus = getEmotoradLifecycleOverviewHeaderRawStatus(row, eventContainerTemplate);
            const {statusText, statusBadgeKind} = resolveEmotoradLifecycleOverviewHeaderStatus(rawStatus);
            const platformDisplay = resolveEmotoradLifecycleOverviewHeaderPlatformDisplay({
                platformFromDisplayNameLookup: platform,
                additionalColumnValues: row.additionalColumnValues,
            });
            return {
                statusText,
                statusBadgeKind,
                customerName,
                platform: platformDisplay,
                assigneeName: assignmentOwnerDisplay === "" ? "-" : assignmentOwnerDisplay,
                dateText: formatDateTime(row.createdAt),
            };
        }

        const assignmentLabel =
            resolveInQueueAssignmentLabel !== undefined ? resolveInQueueAssignmentLabel(row) : assignmentOwnerDisplay === "" ? "Unassigned" : "Assigned";
        const statusText = assignmentLabel;
        const platformDisplay = resolveEmotoradLifecycleOverviewHeaderPlatformDisplay({
            platformFromDisplayNameLookup: platform,
            additionalColumnValues: row.additionalColumnValues,
        });
        return {
            statusText,
            statusBadgeKind: assignmentLabel.toLowerCase() as EventContainerInQueueHeaderBadgeKind,
            customerName,
            platform: platformDisplay,
            assigneeName: resolveAssigneeNameFromOwnerColumn(),
            dateText: formatDateTime(row.createdAt),
        };
    }, [getAdditionalColumns,
        eventContainerTemplate,
        row,
        row.additionalColumnValues,
        row.createdAt,
        useInQueueHeader,
        useEmotoradLifecycleOverviewStatusHeader,
        useAmaronLeadsStatusHeader,
        useAmaronEnquiryStatusHeader,
        resolveInQueueAssignmentLabel,
        inQueueAssignmentOwnerColumnId,
        users,
        userTemplates,
        platformUserDetailsByUserId,
    ]);

    const renderPanelBody = () => {
        if (layout === "focusInline") {
            const detailSections = sections.filter((section) => section.kind === OttopilotEventContainerViewPanelSectionKind.columnGroup);
            const previewNotes = noteItems.slice(0, DAY_PLAN_FOCUS_NOTES_PREVIEW_LIMIT);
            const showViewAllNotes = onViewAllNotes !== undefined;
            return (
                <div className="w-full min-w-0 grid grid-flow-row">
                    {detailSections.map(renderSection)}
                    <AnankeTempCollapsibleSectionWithIcon
                        title="Recent Notes"
                        icon={NoteIcon}
                        iconColor="var(--ds-icon-accent-blue)"
                        iconBackgroundColor={token("color.background.accent.teal.subtlest")}
                        initialIsOpen={true}
                        className="border-t border-ds-border py-ds-space-100 px-ds-space-200"
                        headerTrailing={
                            showViewAllNotes === true ? (
                                <button
                                    type="button"
                                    onClick={onViewAllNotes}
                                    className="shrink-0 cursor-pointer border-0 bg-transparent p-0 text-sm font-medium text-ds-text-subtlest hover:underline"
                                >
                                    view all
                                </button>
                            ) : undefined
                        }
                    >
                        {isSectionContentLoading === true ? (
                            <CollapsibleSectionLoadingSkeleton fieldGridColumnCount={resolvedFieldGridColumnCount} />
                        ) : (
                            <EventContainerViewPanelNotesContent noteItems={previewNotes} />
                        )}
                    </AnankeTempCollapsibleSectionWithIcon>
                </div>
            );
        }

        if (usePanelTabs === true) {
            const detailSections = sections.filter((section) => section.kind === OttopilotEventContainerViewPanelSectionKind.columnGroup);
            return (
                <div className="w-full min-w-0">
                    <AnankeTabs
                        id={uniqueUuid`fdf5e6e7-79ab-45dd-afab-59b066b5fd24`}
                        selected={panelTabIndex}
                        onChange={(value) => {
                            if (panelTabIndex === 2 && value !== 2 && notesDirty === true) {
                                pendingPanelTabIndexRef.current = value;
                                requestNotesTabLeave();
                                return;
                            }
                            setPanelTabIndex(value);
                        }}
                    >
                        <div className="px-ds-space-200">
                            <AnankeTabList>
                                <AnankeTab testId="day-plan-view-panel-details-tab">Details</AnankeTab>
                                <AnankeTab testId="day-plan-view-panel-activity-tab">Activity</AnankeTab>
                                <AnankeTab testId="day-plan-view-panel-notes-tab">Notes</AnankeTab>
                            </AnankeTabList>
                        </div>
                        <AnankeTabPanel>
                            <ViewPanelTabPanelContent>
                                <div className="w-full grid grid-flow-row">{detailSections.map(renderSection)}</div>
                            </ViewPanelTabPanelContent>
                        </AnankeTabPanel>
                        <AnankeTabPanel>
                            <ViewPanelTabPanelContent>
                                <div className="border-t border-ds-border">
                                    <ActivityTimeline eventContainer={row} />
                                </div>
                            </ViewPanelTabPanelContent>
                        </AnankeTabPanel>
                        <AnankeTabPanel>
                            <ViewPanelTabPanelContent>
                                <div className="border-t border-ds-border">
                                    <ListNotesPanelBody
                                        key={notesComposerKey}
                                        context={{
                                            eventContainerId: row.id,
                                            eventContainer: eventContainerForNotes,
                                            createScope: {scopeType: "eventContainer"},
                                            eventContainerSourceLabel: resolvedNotesEventContainerSourceLabel,
                                        }}
                                        eventContainer={eventContainerForNotes}
                                        onEventContainerPatched={(patchedEventContainer) => {
                                            setEventContainerForNotes((previous) => ({
                                                ...previous,
                                                ...patchedEventContainer,
                                            }));
                                        }}
                                        isFullscreen={isExpanded}
                                        onDirtyChange={setNotesDirty}
                                    />
                                </div>
                            </ViewPanelTabPanelContent>
                        </AnankeTabPanel>
                    </AnankeTabs>
                </div>
            );
        }

        return (
            <>
                {sections.map(renderSection)}
                <AnankeTempCollapsibleSectionWithIcon
                    title="Notes"
                    icon={NoteIcon}
                    iconColor="var(--ds-icon-accent-blue)"
                    iconBackgroundColor={token("color.background.accent.teal.subtlest")}
                    initialIsOpen={true}
                    className="border-t border-ds-border py-ds-space-100 px-ds-space-200"
                >
                    <EventContainerViewPanelNotesContent noteItems={noteItems} />
                </AnankeTempCollapsibleSectionWithIcon>
            </>
        );
    };

    if (layout === "focusInline") {
        return (
            <>
                <div className="w-full min-w-0 grid grid-flow-row">{renderPanelBody()}</div>
                {notesTabLeaveModal}
            </>
        );
    }

    if (isExpanded) {
        // Split sections for two-column layout: overview + data-heavy sections on left, lighter ones on right
        // For simplicity, we put all sections in a single column when expanded
        return (
            <>
            <div className="grid grid-flow-row p-ds-space-200">
                <div className="w-full px-ds-space-200 pb-ds-space-150 grid grid-flow-row gap-y-ds-space-100">
                    {useInQueueHeader === true && inQueueHeaderData !== null ? (
                        <>
                            <div className="grid grid-cols-[max-content_max-content] gap-x-ds-space-050">
                                <span
                                    className="inline-flex items-center px-2 py-0.5 text-xs font-medium"
                                    style={{
                                        borderRadius: "3px",
                                        ...(() => {
                                            const {border, background, color} = eventContainerInQueueHeaderBadgeColors(inQueueHeaderData.statusBadgeKind);
                                            return {
                                                border: `0.5px solid ${border}`,
                                                background,
                                                ...(color !== undefined ? {color} : {}),
                                            };
                                        })(),
                                    }}
                                >
                                    {inQueueHeaderData.statusText}
                                </span>
                            </div>
                            <div className="grid grid-flow-col gap-ds-space-100 items-start justify-between">
                                <div className="line-clamp-1 min-w-0">
                                    <AnankeHeading size="large">{inQueueHeaderData.customerName}</AnankeHeading>
                                </div>
                                {onEditClick !== undefined || showEventContainerProfileLink === true ? (
                                    <div className="flex shrink-0 items-center gap-ds-space-050">
                                        {onEditClick !== undefined ? (
                                            <div className="border border-ds-border rounded-[4px] bg-ds-background-neutral-subtle">
                                                <AnankeIconButton
                                                    type="button"
                                                    icon={EditIcon}
                                                    label="Edit"
                                                    appearance="subtle"
                                                    onClick={onEditClick}
                                                />
                                            </div>
                                        ) : null}
                                        {showEventContainerProfileLink === true ? (
                                            <div className="border border-ds-border rounded-[4px] bg-ds-background-neutral-subtle">
                                                <AnankeIconButton
                                                    type="button"
                                                    icon={LinkExternalIcon}
                                                    label="View Details"
                                                    appearance="subtle"
                                                    onClick={navigateToEventContainerDetails}
                                                />
                                            </div>
                                        ) : null}
                                    </div>
                                ) : null}
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-[max-content_max-content] gap-ds-space-100">
                                <div className="flex h-[28px] min-h-[24px] px-ds-space-150 justify-center items-center rounded-[4px] border border-ds-border bg-ds-background-neutral-subtle gap-ds-space-050">
                                    <CalendarIcon
                                        label="Calendar"
                                        color={token("color.icon.information")}
                                    />
                                    <AnankeText>{inQueueHeaderData.dateText}</AnankeText>
                                </div>
                                <div className="flex h-[28px] min-h-[24px] px-ds-space-150 justify-center items-center rounded-[4px] border border-ds-border bg-ds-background-neutral-subtle">
                                    <AnankeText>{inQueueHeaderData.platform}</AnankeText>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="grid grid-cols-[max-content_max-content] gap-x-ds-space-050">
                                <AnankeSimpleTag text={eventContainerStatusToDisplayName(row.status)} />
                                <AnankeSimpleTag
                                    text={eventContainerTemplate.configuration.moduleDisplayNameVariants.singularSentenceCase}
                                    color="blue"
                                />
                            </div>
                            <div className="grid grid-flow-col gap-ds-space-100 items-center justify-between">
                                <AnankeHeading size="medium">
                                    {eventContainerTemplate.configuration.moduleDisplayNameVariants.singularSentenceCase} Details
                                </AnankeHeading>
                                {showEventContainerProfileLink === true ? (
                                    <div className="border border-ds-border rounded-ds-border-radius">
                                        <AnankeIconButton
                                            type="button"
                                            icon={ArrowUpRightIcon}
                                            label="View Details"
                                            appearance="subtle"
                                            onClick={navigateToEventContainerDetails}
                                        />
                                    </div>
                                ) : null}
                            </div>
                        </>
                    )}
                </div>
                <div className="grid grid-flow-row gap-y-ds-space-250 pb-ds-space-200 h-fit">{renderPanelBody()}</div>
            </div>
            {notesTabLeaveModal}
            </>
        );
    }

    return (
        <>
        <div className="grid grid-flow-row pb-ds-space-200">
            <div
                className={`w-full px-ds-space-200 pt-ds-space-050 grid grid-flow-row gap-y-ds-space-100 ${
                    useInQueueHeader === true && hideAssigneeBar !== true ? "pb-0" : "pb-ds-space-150"
                }`}
            >
                {useInQueueHeader === true && inQueueHeaderData !== null ? (
                    <>
                        <div className="grid grid-cols-[max-content_max-content] gap-x-ds-space-050">
                            <span
                                className="inline-flex items-center px-2 py-0.5 text-xs font-medium"
                                style={{
                                    borderRadius: "3px",
                                    ...(() => {
                                        const {border, background, color} = eventContainerInQueueHeaderBadgeColors(inQueueHeaderData.statusBadgeKind);
                                        return {
                                            border: `0.5px solid ${border}`,
                                            background,
                                            ...(color !== undefined ? {color} : {}),
                                        };
                                    })(),
                                }}
                            >
                                {inQueueHeaderData.statusText}
                            </span>
                        </div>
                        <div className="grid grid-flow-col gap-ds-space-100 items-start justify-between">
                            <div className="line-clamp-1 min-w-0">
                                <AnankeHeading size="large">{inQueueHeaderData.customerName}</AnankeHeading>
                            </div>
                            {onEditClick !== undefined || showEventContainerProfileLink === true ? (
                                <div className="flex shrink-0 items-center gap-ds-space-050">
                                    {onEditClick !== undefined ? (
                                        <div className="border border-ds-border rounded-[4px] bg-ds-background-neutral-subtle">
                                            <AnankeIconButton
                                                type="button"
                                                icon={EditIcon}
                                                label="Edit"
                                                appearance="subtle"
                                                onClick={onEditClick}
                                            />
                                        </div>
                                    ) : null}
                                    {showEventContainerProfileLink === true ? (
                                        <div className="border border-ds-border rounded-[4px] bg-ds-background-neutral-subtle">
                                            <AnankeIconButton
                                                type="button"
                                                icon={LinkExternalIcon}
                                                label="View Details"
                                                appearance="subtle"
                                                onClick={navigateToEventContainerDetails}
                                            />
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}
                        </div>
                        <div className="grid grid-cols-[max-content_max-content] gap-ds-space-100">
                            <div className="flex h-[28px] min-h-[24px] px-ds-space-150 justify-center items-center rounded-[4px] border border-ds-border bg-ds-background-neutral-subtle gap-ds-space-050">
                                <CalendarIcon
                                    label="Calendar"
                                    color={token("color.icon.information")}
                                />
                                <AnankeText>{inQueueHeaderData.dateText}</AnankeText>
                            </div>
                            <div className="flex h-[28px] min-h-[24px] px-ds-space-150 justify-center items-center rounded-[4px] border border-ds-border bg-ds-background-neutral-subtle">
                                <AnankeText>{inQueueHeaderData.platform}</AnankeText>
                            </div>
                        </div>
                        {hideAssigneeBar !== true ? (
                            <div className="flex -mx-ds-space-200 px-ds-space-300 py-ds-space-150 justify-between items-center border-b border-ds-border bg-ds-background-information">
                                <div className="flex items-center gap-ds-space-100 min-w-0">
                                    <AnankeAvatar
                                        size="small"
                                        name={inQueueHeaderData.assigneeName}
                                    />
                                    <span className="truncate">
                                        <AnankeText>{inQueueHeaderData.assigneeName}</AnankeText>
                                    </span>
                                </div>
                                <div
                                    className="flex px-ds-space-100 py-ds-space-050 items-center gap-ds-space-075 rounded-[4px]"
                                    style={{background: token("color.icon.inverse")}}
                                >
                                    <span style={{color: token("color.text.information")}}>
                                        <AnankeText
                                            size="small"
                                            color="inherit"
                                        >
                                            Assignee
                                        </AnankeText>
                                    </span>
                                </div>
                            </div>
                        ) : null}
                    </>
                ) : (
                    <>
                        <div className="grid grid-cols-[max-content_max-content] gap-x-ds-space-050">
                            <AnankeSimpleTag text={eventContainerStatusToDisplayName(row.status)} />
                            <AnankeSimpleTag
                                text={eventContainerTemplate.configuration.moduleDisplayNameVariants.singularSentenceCase}
                                color="blue"
                            />
                        </div>
                        <div className="grid grid-flow-col gap-ds-space-100 items-center justify-between">
                            <AnankeHeading size="medium">
                                {eventContainerTemplate.configuration.moduleDisplayNameVariants.singularSentenceCase} Details
                            </AnankeHeading>
                            {showEventContainerProfileLink === true ? (
                                <div className="border border-ds-border rounded-ds-border-radius">
                                    <AnankeIconButton
                                        type="button"
                                        icon={ArrowUpRightIcon}
                                        label="View Details"
                                        appearance="subtle"
                                        onClick={navigateToEventContainerDetails}
                                    />
                                </div>
                            ) : null}
                        </div>
                    </>
                )}
            </div>

            {useInQueueHeader !== true && (
                <AnankeTempVerticalSpacer
                    above="formRow"
                    below="formRow"
                />
            )}

            <div className="w-full grid grid-flow-row">{renderPanelBody()}</div>
        </div>
        {notesTabLeaveModal}
        </>
    );
}
